import { Card, CardContent } from "@getgreenline/blaze-ui";
import { useFormikContext } from "formik";
import { AlertTriangle } from "lucide-react";
import { adFormatLabels, pacingLabels, pricingLabels } from "@/app-config";
import { usePlacementCatalog } from "@/hooks/use-placement-catalog";
import { getBudgetSummary } from "@/utils/budget";
import { type CampaignFormValues } from "../campaign-form";
import { CardBadge } from "../../shared/card-badge";
import { StepAlert } from "../../shared/step-alert";
import { StepHeader } from "../../shared/step-header";

type ReviewLaunchStepProps = {
  isSubmitting?: boolean;
  submitErrorMessage?: string;
};

function ReviewSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="tw:mt-5">
      <h2 className="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
        {title}
      </h2>
      <div className="tw:mt-2">{children}</div>
    </section>
  );
}

function ReviewField({
  label,
  value,
  accent = false,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <Card className="tw:shadow-none tw:px-4 tw:py-3">
      <CardContent className="tw:p-0">
        <p className="tw:text-xs tw:text-muted-foreground">{label}</p>
        <p
          className={
            accent
              ? "tw:text-sm tw:font-semibold tw:text-sky-700"
              : "tw:text-sm tw:font-semibold tw:text-foreground"
          }
        >
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

export function ReviewLaunchStep({
  isSubmitting = false,
  submitErrorMessage,
}: ReviewLaunchStepProps) {
  const { values } = useFormikContext<CampaignFormValues>();
  const { data: channels = [] } = usePlacementCatalog();

  const selectedBrand = values.brand;
  const selectedChannels = channels.filter((channel) =>
    values.placementChannels.includes(channel.id),
  );
  const selectedZones = selectedChannels.flatMap((channel) =>
    channel.zones.filter((zone) => values.placementZones.includes(zone.id)),
  );
  const { formattedBudget, dailyBudget } = getBudgetSummary(values);
  const usesCustomPacing = values.pacing === "custom";

  return (
    <section className="tw:max-w-5xl">
      <StepHeader
        title="Review & launch"
        description="Your campaign will be reviewed for compliance and go live within 30 minutes of submission."
      />

      <StepAlert
        show
        tone="warning"
        leadingVisual={
          <AlertTriangle className="tw:h-4 tw:w-4 tw:text-amber-700" />
        }
        message="Compliance reminder: By launching you confirm all advertised products are licensed, correctly labelled, and legally available in each dispensary's jurisdiction. Blaze Ads serves impressions only on compliant, age-verified menus."
      />

      <StepAlert
        show={isSubmitting}
        tone="info"
        message="Launching campaign..."
      />

      <StepAlert
        show={Boolean(submitErrorMessage)}
        message={submitErrorMessage}
      />

      <ReviewSection title="Campaign">
        <div className="tw:grid tw:grid-cols-2 tw:gap-3">
          <ReviewField label="Brand" value={selectedBrand?.name ?? "Brand"} />
          <ReviewField
            label="Ad format"
            value={adFormatLabels[values.adFormat] ?? "Ad format"}
            accent
          />
          <div className="tw:col-span-2">
            <ReviewField
              label="Campaign name"
              value={values.campaignName || "Campaign name"}
            />
          </div>
        </div>
      </ReviewSection>

      <ReviewSection title="Placement">
        <div className="tw:space-y-3">
          <ReviewField
            label="Channel"
            value={
              selectedChannels.map((channel) => channel.title).join(", ") ||
              "No channels"
            }
          />
          <div className="tw:flex tw:flex-wrap tw:gap-2">
            {selectedZones.map((zone) => (
              <CardBadge
                key={zone.id}
                variant="secondary"
                className="tw:bg-sky-100 tw:text-sky-700"
              >
                {zone.title}
              </CardBadge>
            ))}
          </div>
        </div>
      </ReviewSection>

      <ReviewSection title="Products">
        <div className="tw:grid tw:grid-cols-2 tw:gap-3">
          <ReviewField
            label="Minimum price"
            value={
              values.minimumPrice.trim().length > 0
                ? `$${values.minimumPrice}`
                : "No minimum"
            }
          />
        </div>
      </ReviewSection>

      <ReviewSection title="Budget">
        <div className="tw:grid tw:grid-cols-2 tw:gap-3">
          <ReviewField label="Total budget" value={formattedBudget} />
          <ReviewField
            label={
              values.pacing === "even"
                ? "Daily distribution"
                : values.pacing === "none"
                  ? "Daily cap"
                  : "Daily pacing cap"
            }
            value={
              usesCustomPacing
                ? `$${values.dailyPacing} / day`
                : values.pacing === "none"
                  ? "No daily cap"
                  : dailyBudget > 0
                    ? `~$${dailyBudget} / day`
                    : "~$0 / day"
            }
          />
          <ReviewField
            label="Pacing"
            value={pacingLabels[values.pacing] ?? "Pacing"}
          />
          <ReviewField
            label="Pricing model"
            value={pricingLabels[values.pricingModel] ?? "Pricing model"}
          />
        </div>
      </ReviewSection>
    </section>
  );
}
